import { ApiError, apiRequest } from '../api/client';
import type { Zone } from '../customers/api';

export const INTERNAL_USER_STATUSES = ['INVITED', 'ACTIVE', 'SUSPENDED', 'INACTIVE'] as const;
export type InternalUserStatus = (typeof INTERNAL_USER_STATUSES)[number];

/** Matches `apps/api` `UserSummary` (`user-management.repository.ts`). */
export interface UserListItem {
  id: string;
  email: string;
  fullName: string;
  status: InternalUserStatus;
  roleCodes: string[];
  lastLoginAt: string | null;
  totpEnabled: boolean;
  /** Assigned points of sale ("Punto de venta"); empty means every point. */
  operatingUnits: { id: string; name: string }[];
}

/** Matches `apps/api` `UserDetail`. */
export interface UserDetail extends UserListItem {
  phone: string | null;
  zones: Zone[];
}

export interface ListUsersFilter {
  search?: string | undefined;
  status?: InternalUserStatus | undefined;
  roleId?: string | undefined;
  limit: number;
  offset: number;
}

export interface ListUsersResult {
  items: UserListItem[];
  total: number;
}

/** Matches `apps/api` `inviteInternalUserSchema`. */
export interface InviteUserInput {
  email: string;
  fullName: string;
  roleIds: string[];
  zones: Zone[];
  /** Omitted (or empty) = every point, same semantics as `zones`. */
  operatingUnitIds?: string[];
}

/**
 * Matches `apps/api` `InvitationDelivery` (`invitation-notifier.port.ts`).
 * `NOT_SENT` is not a failure: the invitation exists, and `activationUrl`
 * (when the actor may see it) is how to deliver it.
 */
export type InvitationDelivery =
  | { status: 'SENT' }
  | {
      status: 'NOT_SENT';
      reason: 'EMAIL_NOT_CONFIGURED' | 'LOGGING_ONLY';
      activationUrl?: string;
    }
  | { status: 'NOT_SENT'; reason: 'LINK_WITHHELD' };

export interface InviteUserResult {
  userId: string;
  invitationId: string;
  expiresAt: string;
  delivery: InvitationDelivery;
}

/** Matches `apps/api` `updateUserSchema`: never `email`, `status` nor `roleIds`. */
export interface UpdateUserInput {
  fullName?: string;
  phone?: string | null;
  zones?: Zone[];
  /** `[]` returns the user to every point. */
  operatingUnitIds?: string[];
}

export interface StoredSession {
  id: string;
  deviceId: string | null;
  deviceName: string | null;
  userAgent: string | null;
  createdAt: string;
  expiresAt: string;
}

/** Matches `apps/api` `RoleListItem` (`list-roles.use-case.ts`), the fields this feature needs. */
export interface RoleOption {
  id: string;
  code: string;
  name: string;
  permissions: string[];
  isSystem: boolean;
}

export function listUsers(filter: ListUsersFilter): Promise<ListUsersResult> {
  const params = new URLSearchParams();

  if (filter.search !== undefined && filter.search !== '') {
    params.set('search', filter.search);
  }

  if (filter.status !== undefined) {
    params.set('status', filter.status);
  }

  if (filter.roleId !== undefined) {
    params.set('roleId', filter.roleId);
  }

  params.set('limit', String(filter.limit));
  params.set('offset', String(filter.offset));

  return apiRequest<ListUsersResult>(`/users?${params.toString()}`);
}

export function getUser(id: string): Promise<UserDetail> {
  return apiRequest<UserDetail>(`/users/${id}`);
}

export function inviteUser(input: InviteUserInput): Promise<InviteUserResult> {
  return apiRequest<InviteUserResult>('/users', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function resendInvitation(id: string): Promise<InviteUserResult> {
  return apiRequest<InviteUserResult>(`/users/${id}/resend-invitation`, { method: 'POST' });
}

export function updateUser(id: string, input: UpdateUserInput): Promise<UserDetail> {
  return apiRequest<UserDetail>(`/users/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export function updateUserRoles(id: string, roleIds: string[]): Promise<void> {
  return apiRequest<void>(`/users/${id}/roles`, {
    method: 'PATCH',
    body: JSON.stringify({ roleIds }),
  });
}

export function deactivateUser(id: string, reason?: string): Promise<UserDetail> {
  return apiRequest<UserDetail>(`/users/${id}/deactivate`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export function reactivateUser(id: string): Promise<UserDetail> {
  return apiRequest<UserDetail>(`/users/${id}/reactivate`, { method: 'POST' });
}

export function listUserSessions(id: string): Promise<StoredSession[]> {
  return apiRequest<StoredSession[]>(`/users/${id}/sessions`);
}

export function revokeUserSession(id: string, tokenId: string): Promise<void> {
  return apiRequest<void>(`/users/${id}/sessions/${tokenId}`, { method: 'DELETE' });
}

export function revokeAllUserSessions(id: string): Promise<void> {
  return apiRequest<void>(`/users/${id}/sessions`, { method: 'DELETE' });
}

export function resetUserTotp(id: string): Promise<UserDetail> {
  return apiRequest<UserDetail>(`/users/${id}/totp`, { method: 'DELETE' });
}

export function listRoles(): Promise<RoleOption[]> {
  return apiRequest<RoleOption[]>('/roles');
}

/**
 * `code` field driven, same convention as `mapPortalAccessError`. Every
 * code the API's `UsersExceptionFilter`/`RolesExceptionFilter` can send for
 * this feature gets an explicit Spanish message.
 */
export function mapUserError(error: unknown): string {
  if (error === undefined || error === null) {
    return '';
  }

  if (!(error instanceof ApiError)) {
    return 'Ocurrió un error de conexión. Intentá de nuevo.';
  }

  const code = (error.body as { code?: string } | null)?.code;

  if (code === 'EMAIL_ALREADY_REGISTERED') {
    return 'Ya existe un usuario registrado con ese email.';
  }

  if (code === 'PRIVILEGE_ESCALATION') {
    return 'No podés otorgar permisos que vos mismo no tenés.';
  }

  if (code === 'LAST_SUPER_ADMIN_GUARD') {
    return 'No se puede quitar el rol Superadministrador al único superadministrador activo restante.';
  }

  if (code === 'SELF_DEACTIVATION_FORBIDDEN') {
    return 'No podés desactivar tu propia cuenta.';
  }

  if (code === 'NO_PENDING_INVITATION') {
    return 'Este usuario no tiene una invitación pendiente.';
  }

  if (code === 'USER_STATUS_CONFLICT') {
    return 'Este usuario ya está activo.';
  }

  if (code === 'OPERATING_UNIT_ASSIGNMENT_FORBIDDEN') {
    return 'No podés asignar puntos de venta fuera de tu propio alcance.';
  }

  if (code === 'OPERATING_UNIT_NOT_FOUND') {
    return 'Alguno de los puntos de venta elegidos no existe o está inactivo.';
  }

  if (code === 'INVITATION_RATE_LIMITED') {
    return 'Enviaste demasiadas invitaciones. Esperá unos minutos y volvé a intentar.';
  }

  if (error.status === 403) {
    return 'No tenés permiso para realizar esta acción.';
  }

  if (error.status === 404) {
    return 'El usuario ya no existe.';
  }

  if (error.status === 400 || error.status === 422) {
    return 'Revisá los datos ingresados e intentá de nuevo.';
  }

  return 'Ocurrió un error inesperado. Intentá de nuevo.';
}
